import * as React from 'react';
import { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form } from 'reactstrap';
import { ClientData } from '../../models';
import { getMyTenantClientAsync, updateMyTenantClientAsync } from '../../ajax/requests';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { TextInputField, PhoneNumberField } from '../../components/Forms/fields';
import { Row } from '../../components/Forms/table/Row';
import { Col } from '../../components/Forms/table/Column';
import { splitPhoneCodeFromNumber } from '../../components/Forms/fields/MultiplePhoneNumberField';
import AppButton from '../../components/AppButton';

interface UpdateInvoiceInfoModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave?: () => void;
}

const UpdateInvoiceInfoModal: React.FC<UpdateInvoiceInfoModalProps> = ({ isOpen, onClose, onSave }) => {
    const t = useTranslations();
    const [clientData, setClientData] = useState<ClientData | null>(null);
    const [originalData, setOriginalData] = useState<ClientData | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (isOpen) {
            loadClientData();
        }
    }, [isOpen]);

    const loadClientData = async () => {
        setLoading(true);
        try {
            const data = await getMyTenantClientAsync();
            setClientData(data);
            setOriginalData(data);
        } catch (error) {
            console.error('Failed to load client data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!clientData) return;

        if (JSON.stringify(clientData) === JSON.stringify(originalData)) {
            onClose();
            onSave?.();
            return;
        }

        setSaving(true);
        try {
            await updateMyTenantClientAsync(clientData);
            onClose();
            onSave?.();
        } catch (error) {
            console.error('Failed to save client data:', error);
        } finally {
            setSaving(false);
        }
    };

    const updateField = <K extends keyof ClientData>(field: K, value: ClientData[K]) => {
        if (!clientData) return;
        setClientData({
            ...clientData,
            [field]: value,
        });
    };

    const phone = splitPhoneCodeFromNumber(clientData?.phoneNumbers?.[0] ?? '')

    return (
        <Modal isOpen={isOpen} toggle={onClose} size="lg">
            <ModalHeader toggle={onClose}>
                {t(TK.updateMyInvoiceInformation)}
            </ModalHeader>
            <ModalBody>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: 'var(--spacer-L)', color: 'var(--text-muted)', fontSize: '14px' }}>
                        {t(TK.loading)}...
                    </div>
                ) : clientData ? (
                    <Form style={{display: 'flex', marginTop: 'var(--spacer-S)', flexDirection: 'column', gap: 'var(--spacer-M)'}}>
                        <Row>
                            <Col sm={12}>
                                <TextInputField
                                    label={t(TK.name)}
                                    value={clientData.name}
                                    onChange={(value) => updateField('name', value)}
                                    placeholder={t(TK.name)}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col sm={12} md={6}>
                                <TextInputField
                                    label={t(TK.email)}
                                    type="email"
                                    value={clientData.email}
                                    onChange={(value) => updateField('email', value)}
                                    placeholder={t(TK.email)}
                                />
                            </Col>
                            <Col sm={12} md={6}>
                                <TextInputField
                                    label={t(TK.taxNumber)}
                                    value={clientData.taxNumber}
                                    onChange={(value) => updateField('taxNumber', value)}
                                    placeholder={t(TK.taxNumber)}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col sm={12} md={6}>
                                <PhoneNumberField
                                    label={t(TK.phoneNumber)}
                                    value={phone.number}
                                    prefix={phone.prefix}
                                    onChange={(value, prefix) => updateField('phoneNumbers', [`${prefix}${value}`])}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col sm={12}>
                                <TextInputField
                                    label={t(TK.address)}
                                    value={clientData.addressLine1}
                                    onChange={(value) => updateField('addressLine1', value)}
                                    placeholder={t(TK.address)}
                                />
                                <TextInputField
                                    value={clientData.addressLine2}
                                    onChange={(value) => updateField('addressLine2', value)}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col sm={12} md={6}>
                                <TextInputField
                                    label={t(TK.zipCode)}
                                    value={clientData.zipCode}
                                    onChange={(value) => updateField('zipCode', value)}
                                    placeholder={t(TK.zipCode)}
                                />
                            </Col>
                            <Col sm={12} md={6}>
                                <TextInputField
                                    label={t(TK.city)}
                                    value={clientData.city}
                                    onChange={(value) => updateField('city', value)}
                                    placeholder={t(TK.city)}
                                />
                            </Col>
                        </Row>
                    </Form>
                ) : null}
            </ModalBody>
            <ModalFooter>
                <AppButton color="secondary" onClick={onClose} disabled={saving}>
                    {t(TK.cancel)}
                </AppButton>
                <AppButton color="success" onClick={handleSave} disabled={saving || !clientData}>
                    {saving ? t(TK.saving) : t(TK.save)}
                </AppButton>
            </ModalFooter>
        </Modal>
    );
};

export default UpdateInvoiceInfoModal;
